import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';
import { buildSeoTitle, buildMetaDescription } from 'shared/lib/seoHelpers';
import { Submit } from 'shared/components/Form';
import Page from 'userportal/components/Page';
import Subheader from 'shared/components/Subheader';
import Score from 'shared/components/Score';
import ScoreDims from 'shared/components/Score/Dims';
import Tag from 'shared/components/Tag';
import LevelsHint from 'shared/components/LevelsHint';
import { userportal as routes, backoffice, createBackofficeLink, createUserportalLink } from 'shared/routes';
import { ALL_JOBS_LINK } from 'shared/constants';
import { selectors } from 'userportal/ducks/auth';
import styles from './About.module.scss';

// Illustrative sample job shown in the intro card (language-neutral parts).
const SAMPLE_SCORE = 69;
const SAMPLE_DIMS = { capabilities: 71, overview: 84, trajectory: 66, context: 48, domain: 70 };
// Order of the five dimensions in the "5 dimenzí Skilleto" section.
const DIMENSIONS = ['capabilities', 'overview', 'trajectory', 'context', 'domain'];
const SAMPLE_SKILLS = [
  { slug: 'react', title: 'React', level: 3 },
  { slug: 'typescript', title: 'TypeScript', level: 2 },
  { slug: 'vite', title: 'Vite', level: 1 },
];

const About = () => {
  const { t } = useTranslation('userportal');
  const navigate = useNavigate();
  const isLoggedIn = useSelector(selectors.isLoggedIn);

  const onSignup = (event) => {
    event.preventDefault();
    navigate(routes.signup);
  };

  return (
    <Page
      initialContainer={false}
      subheader={(
        <Subheader center elementType="h1" title={t('page.about.title')}>
          <span>{t('page.about.subtitle')}</span>
        </Subheader>
      )}
      helmetTitle={buildSeoTitle(t, t('page.about.title'))}
      helmetDescription={buildMetaDescription(t('page.about.intro.p1'))}
      seoUrl={createUserportalLink(routes.about)}
    >
      <div className="container">
        <div className={styles.intro}>
          <div>
            <h2>{t('page.about.intro.heading')}</h2>
            <div className={styles.accent} />
            <p>
              <Trans i18nKey="userportal:page.about.intro.p1">
                <strong />
              </Trans>
            </p>
            <p>
              <Trans i18nKey="userportal:page.about.intro.p2">
                <strong />
              </Trans>
            </p>
            {!isLoggedIn && (
              <Submit title={t('common:button.signupNow')} onClick={onSignup} className={styles.signup} />
            )}
          </div>
          <aside className={styles.jobCard}>
            <div className={styles.cardKicker}>{t('page.about.sample.kicker')}</div>
            <div className={styles.jobHeader}>
              <Score id="about-sample" score={SAMPLE_SCORE} type="common" className={styles.score} />
              <div>
                <div className={styles.jobTitle}>{t('page.about.sample.jobTitle')}</div>
                <div className={styles.jobMeta}>{t('page.about.sample.jobMeta')}</div>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.cardSectionTitle}>{t('page.about.sample.dimsTitle')}</div>
            <ScoreDims
              dims={SAMPLE_DIMS}
              showValue
              stacked
              tooltipOnIcon
              getLabel={key => t(`common:score.dim.${key}`)}
              getTooltip={(key, value) => (
                <>
                  <strong>{t(`common:score.dim.${key}`)}: {value}%</strong>
                  <br />
                  {t(`common:score.dimTooltip.${key}`)}
                </>
              )}
            />
            <div className={styles.divider} />
            <div className={styles.skillsHeader}>
              <span className={styles.cardSectionTitle}>{t('page.about.sample.skillsTitle')}</span>
              <LevelsHint className={styles.levelsHint} />
            </div>
            <div className={styles.skills}>
              {SAMPLE_SKILLS.map(skill => (
                <Tag key={skill.slug} isSmall noTooltip slug={skill.slug} type="skill" level={skill.level}>
                  {skill.title}
                </Tag>
              ))}
              <Tag isSmall noIcon noTooltip>{t('job.restSkills')} (4)</Tag>
            </div>
            <div className={styles.salary}>
              <span>{t('page.about.sample.salaryLabel')}</span>
              <span className={styles.salaryValue}>{t('page.about.sample.salaryValue')}</span>
            </div>
          </aside>
        </div>
      </div>

      <div className={styles.dimsSection}>
        <div className="container">
          <h2>{t('page.about.dims.heading')}</h2>
          <p className={styles.dimsTagline}>{t('page.about.dims.tagline')}</p>
          <p className={styles.dimsIntro}>{t('page.about.dims.intro')}</p>
          <div className={styles.dimsGrid}>
            {DIMENSIONS.map(key => (
              <div key={key} className={styles.dimCard}>
                <div className={styles.dimCardHeader}>
                  <h3>{t(`common:score.dim.${key}`)}</h3>
                  {key === 'capabilities' && <span className={styles.dimBadge}>{t('page.about.dims.topWeight')}</span>}
                </div>
                <p className={styles.dimQuestion}>{t(`page.about.dims.${key}Question`)}</p>
                <p>{t(`page.about.dims.${key}Text`)}</p>
              </div>
            ))}
          </div>
          <div className={styles.dimNotes}>
            <div className={cn(styles.dimNote, styles.dimNoteGate)}>
              <div className={styles.dimNoteHeader}>
                <span className={styles.dimNoteIcon}><i className="la la-minus" /></span>
                <h3>{t('page.about.dims.gateTitle')}</h3>
              </div>
              <p>{t('page.about.dims.gateText')}</p>
            </div>
            <div className={styles.dimNote}>
              <div className={styles.dimNoteHeader}>
                <span className={styles.dimNoteIcon}><i className="la la-chart-bar" /></span>
                <h3>{t('page.about.dims.mapTitle')}</h3>
              </div>
              <p>{t('page.about.dims.mapText')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.featuresSection}>
        <div className="container">
          <h2>{t('page.about.features.heading')}</h2>
          <p className={styles.featuresSubtitle}>{t('page.about.features.subtitle')}</p>
          <div className={styles.featuresGrid}>
            {[1, 2, 3, 4, 5, 6].map(n => (
              <div key={n} className={cn(styles.featureCard, { [styles.highlighted]: n === 3 })}>
                {n === 3 && <span className={styles.onlyHere}>{t('page.about.features.onlyHere')}</span>}
                <div className={styles.featureCardHeader}>
                  <span className={styles.featureNumber}>{n}</span>
                  <h3>{t(`page.about.features.item${n}Title`)}</h3>
                </div>
                <p>{t(`page.about.features.item${n}Text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.panelsSection}>
        <div className="container">
          <div className={styles.panels}>
            <div className={styles.panel}>
              <span className={styles.badge}>{t('page.about.candidates.badge')}</span>
              <h2>{t('page.about.candidates.heading')}</h2>
              <p>{t('page.about.candidates.text')}</p>
              <div className={styles.steps}>
                {[1, 2, 3].map(n => (
                  <div key={n} className={styles.step}>
                    <span className={styles.stepNumber}>{n}</span>
                    <span>{t(`page.about.candidates.step${n}`)}</span>
                  </div>
                ))}
              </div>
              <div className={styles.panelCtas}>
                <Link to={routes.howItWorks} className={styles.buttonPrimary}>{t('footer.howItWorks')}</Link>
                <Link to={ALL_JOBS_LINK} className={styles.textLink}>{t('page.about.candidates.browseJobs')}</Link>
              </div>
            </div>
            <div className={cn(styles.panel, styles.employersPanel)}>
              <span className={styles.badge}>{t('page.about.employers.badge')}</span>
              <h2>{t('page.about.employers.heading')}</h2>
              <p>{t('page.about.employers.text')}</p>
              <div className={styles.steps}>
                {[1, 2, 3].map(n => (
                  <div key={n} className={styles.step}>
                    <i className={cn('la la-check-circle', styles.bullet)} />
                    <span>{t(`page.about.employers.point${n}`)}</span>
                  </div>
                ))}
              </div>
              <div className={styles.panelCtas}>
                <a href={createBackofficeLink(backoffice.signup)} className={cn(styles.buttonPrimary, styles.buttonPink)}>
                  {t('navigation.forEmployers')}
                </a>
                <a href={createBackofficeLink(backoffice.root)} className={styles.textLink}>
                  {t('page.about.employers.link')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default About;
